import axios from 'axios';
import { Track, Playlist, User, Artist, Album } from '../models';
import { authService } from './auth';

// Base URL for Spotify API
const API_BASE_URL = 'https://api.spotify.com/v1';

// Helper function to make authenticated requests
const apiRequest = async <T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  data?: any
): Promise<T> => {
  const token = await authService.getAccessToken();
  
  if (!token) {
    throw new Error('No access token available');
  }
  
  const url = `${API_BASE_URL}/${endpoint}`;
  
  try {
    const response = await axios({
      method,
      url,
      data,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error(`Error in ${method} ${endpoint}:`, error);
    throw error;
  }
};

// Spotify API service
export class SpotifyService {
  // Get user's profile
  public async getCurrentUser(): Promise<User> {
    try {
      const data = await apiRequest<any>('me');
      
      return {
        id: data.id,
        displayName: data.display_name,
        email: data.email,
        images: data.images || [],
        country: data.country,
        product: data.product,
        followers: data.followers?.total,
        uri: data.uri,
      };
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }
  
  // Get user's playlists
  public async getUserPlaylists(limit: number = 50): Promise<Playlist[]> {
    try {
      const data = await apiRequest<any>(`me/playlists?limit=${limit}`);
      
      return data.items.map((item: any) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        owner: {
          id: item.owner.id,
          displayName: item.owner.display_name,
        },
        public: item.public,
        collaborative: item.collaborative,
        tracks: [],
        imageUrl: item.images && item.images.length > 0 ? item.images[0].url : undefined,
        uri: item.uri,
        totalTracks: item.tracks.total,
      }));
    } catch (error) {
      console.error('Error fetching user playlists:', error);
      throw error;
    }
  }
  
  // Get a specific playlist with tracks
  public async getPlaylist(playlistId: string): Promise<Playlist> {
    try {
      const data = await apiRequest<any>(`playlists/${playlistId}`);
      
      const tracks = data.tracks.items.map((item: any) => this.parseTrackObject(item.track));
      
      return {
        id: data.id,
        name: data.name,
        description: data.description,
        owner: {
          id: data.owner.id,
          displayName: data.owner.display_name,
        },
        public: data.public,
        collaborative: data.collaborative,
        tracks,
        imageUrl: data.images && data.images.length > 0 ? data.images[0].url : undefined,
        uri: data.uri,
        totalTracks: data.tracks.total,
      };
    } catch (error) {
      console.error(`Error fetching playlist ${playlistId}:`, error);
      throw error;
    }
  }
  
  // Create a new playlist
  public async createPlaylist(
    name: string,
    description: string,
    isPublic: boolean = true
  ): Promise<Playlist> {
    try {
      const user = await this.getCurrentUser();
      
      const data = await apiRequest<any>(
        `users/${user.id}/playlists`,
        'POST',
        {
          name,
          description,
          public: isPublic,
        }
      );
      
      return {
        id: data.id,
        name: data.name,
        description: data.description,
        owner: {
          id: data.owner.id,
          displayName: data.owner.display_name,
        },
        public: data.public,
        collaborative: data.collaborative,
        tracks: [],
        imageUrl: data.images && data.images.length > 0 ? data.images[0].url : undefined,
        uri: data.uri,
        totalTracks: 0,
      };
    } catch (error) {
      console.error('Error creating playlist:', error);
      throw error;
    }
  }
  
  // Add tracks to a playlist
  public async addTracksToPlaylist(
    playlistId: string,
    trackUris: string[]
  ): Promise<boolean> {
    try {
      await apiRequest<any>(
        `playlists/${playlistId}/tracks`,
        'POST',
        {
          uris: trackUris,
        }
      );
      
      return true;
    } catch (error) {
      console.error(`Error adding tracks to playlist ${playlistId}:`, error);
      throw error;
    }
  }
  
  // Get user's recently played tracks
  public async getRecentlyPlayed(limit: number = 20): Promise<Track[]> {
    try {
      const data = await apiRequest<any>(`me/player/recently-played?limit=${limit}`);
      
      return data.items.map((item: any) => this.parseTrackObject(item.track));
    } catch (error) {
      console.error('Error fetching recently played tracks:', error);
      throw error;
    }
  }
  
  // Search for tracks, artists, albums
  public async search(
    query: string,
    type: 'track' | 'artist' | 'album' | 'playlist' | string = 'track',
    limit: number = 20
  ): Promise<any> {
    try {
      const data = await apiRequest<any>(
        `search?q=${encodeURIComponent(query)}&type=${type}&limit=${limit}`
      );
      
      const results: any = {};
      
      if (data.tracks) {
        results.tracks = data.tracks.items.map((track: any) => this.parseTrackObject(track));
      }
      
      if (data.artists) {
        results.artists = data.artists.items.map((artist: any) => ({
          id: artist.id,
          name: artist.name,
          uri: artist.uri,
          imageUrl: artist.images && artist.images.length > 0 ? artist.images[0].url : undefined,
        }));
      }
      
      if (data.albums) {
        results.albums = data.albums.items.map((album: any) => ({
          id: album.id,
          name: album.name,
          artists: album.artists.map((artist: any) => ({
            id: artist.id,
            name: artist.name,
            uri: artist.uri,
          })),
          releaseDate: album.release_date,
          totalTracks: album.total_tracks,
          uri: album.uri,
          images: album.images,
        }));
      }
      
      if (data.playlists) {
        results.playlists = data.playlists.items.map((playlist: any) => ({
          id: playlist.id,
          name: playlist.name,
          description: playlist.description,
          owner: {
            id: playlist.owner.id,
            displayName: playlist.owner.display_name,
          },
          public: playlist.public,
          collaborative: playlist.collaborative,
          tracks: [],
          imageUrl: playlist.images && playlist.images.length > 0 ? playlist.images[0].url : undefined,
          uri: playlist.uri,
          totalTracks: playlist.tracks.total,
        }));
      }
      
      return results;
    } catch (error) {
      console.error(`Error searching for ${type} with query "${query}":`, error);
      throw error;
    }
  }
  
  // Get recommendations based on seed tracks, artists, or genres
  public async getRecommendations(
    seedTracks?: string[],
    seedArtists?: string[],
    seedGenres?: string[],
    limit: number = 20
  ): Promise<Track[]> {
    try {
      const params = new URLSearchParams();
      
      if (seedTracks && seedTracks.length > 0) {
        params.append('seed_tracks', seedTracks.join(','));
      }
      
      if (seedArtists && seedArtists.length > 0) {
        params.append('seed_artists', seedArtists.join(','));
      }
      
      if (seedGenres && seedGenres.length > 0) {
        params.append('seed_genres', seedGenres.join(','));
      }
      
      params.append('limit', limit.toString());
      
      const data = await apiRequest<any>(`recommendations?${params.toString()}`);
      
      return data.tracks.map((track: any) => this.parseTrackObject(track));
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      throw error;
    }
  }
  
  // Get a specific track by ID
  public async getTrack(trackId: string): Promise<Track | null> {
    try {
      const data = await apiRequest<any>(`tracks/${trackId}`);
      return this.parseTrackObject(data);
    } catch (error) {
      console.error(`Error fetching track ${trackId}:`, error);
      throw error;
    }
  }
  
  // Get user's top tracks or artists
  public async getUserTop(
    type: 'tracks' | 'artists',
    timeRange: 'short_term' | 'medium_term' | 'long_term' = 'medium_term',
    limit: number = 20
  ): Promise<Track[] | Artist[]> {
    try {
      const data = await apiRequest<any>(
        `me/top/${type}?time_range=${timeRange}&limit=${limit}`
      );
      
      if (type === 'tracks') {
        return data.items.map((track: any) => this.parseTrackObject(track));
      } else {
        return data.items.map((artist: any) => ({
          id: artist.id,
          name: artist.name,
          uri: artist.uri,
          imageUrl: artist.images && artist.images.length > 0 ? artist.images[0].url : undefined,
        }));
      }
    } catch (error) {
      console.error(`Error fetching user's top ${type}:`, error);
      throw error;
    }
  }
  
  // Helper method to parse track objects from Spotify API
  private parseTrackObject(track: any): Track {
    return {
      id: track.id,
      name: track.name,
      artists: track.artists.map((artist: any) => ({
        id: artist.id,
        name: artist.name,
        uri: artist.uri,
      })),
      album: {
        id: track.album.id,
        name: track.album.name,
        artists: track.album.artists.map((artist: any) => ({
          id: artist.id,
          name: artist.name,
          uri: artist.uri,
        })),
        releaseDate: track.album.release_date,
        totalTracks: track.album.total_tracks,
        uri: track.album.uri,
        images: track.album.images,
      },
      uri: track.uri,
      durationMs: track.duration_ms,
      explicit: track.explicit,
      popularity: track.popularity,
      previewUrl: track.preview_url,
      imageUrl: track.album.images && track.album.images.length > 0 ? track.album.images[0].url : undefined,
    };
  }
}

// Create a singleton instance
export const spotifyService = new SpotifyService();
